import {
  Box,
  Button,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import {
  addNewWebForm,
  deleteWebForm,
  editWebForm,
  getWebForms,
} from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faX } from "@fortawesome/free-solid-svg-icons";
import AppModal from "../../../components/AppModal/AppModal";
import { confirmDialog } from "../../../components/ConfirmDialog/confirmDialog";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

type FormValues = {
  formName: string;
  formLink: string;
  isAccessibleToAll: any;
};

const WebForms = () => {
    const { state } = useAuth();
    const [webForms, setWebForms] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [formToEdit, setFormToEdit] = useState<FormValues & { id?: string } | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        control,
    } = useForm<FormValues>();

    const {
        register: editRegister,
        handleSubmit: handleEditSubmit,
        control: controlEdit,
        reset: resetEdit,
    } = useForm<FormValues>({
        defaultValues: {
            isAccessibleToAll: true
        }
    });

    const getAllWebForms = async () => {
        const response = await getWebForms(state.user?.token);
        setWebForms(response);
    };

    const onSubmit = async (data: FormValues) => {
        const response = await addNewWebForm(state.user?.token, data);
        if (response) {
            toast.success("Web Form added successfully!");
            getAllWebForms();
            setIsOpen(false);
            reset({
                formName: '',
                formLink: '',
                isAccessibleToAll: false,
            });
        } else {
            toast.error("Something went wrong!");
        }
    };

    const handleEdit = (formData: any) => {
        console.log(formData, '')
        setFormToEdit(formData);
        setEditModalOpen(true);
        resetEdit({
            formName: formData.formName,
            formLink: formData.formLink,
            isAccessibleToAll: formData.isAccessibleToAll,
        });
    };

    const handleUpdate = async (data: FormValues) => {
        if (!formToEdit?.id) return;
        const updatedData = { ...data, id: formToEdit.id };
        const response = await editWebForm(state.user?.token, updatedData);
        if (response) {
            toast.success("Web Form updated successfully!");
            getAllWebForms();
            setEditModalOpen(false);
            setFormToEdit(null);
        } else {
            toast.error("Something went wrong!");
        }
    };

    const handleDelete = async (id: any) => {
        const response = await deleteWebForm(state.user?.token, id);
        if (response) {
            getAllWebForms();
            toast.success("Web Form deleted successfully");
        } else {
            toast.error("Something went wrong!");
        }
    };

    useEffect(() => {
        getAllWebForms();
    }, []);

    const columns: any = [
        { field: "formName", headerName: "Form Name", flex: 1 },
        {
          field: "formLink",
          headerName: "Form Link",
          headerAlign: "center",
          align: "center",
          flex: 2,
        },
        {
          field: "isAccessibleToAll",
          headerName: "Accessible",
          headerAlign: "center",
          align: "center",
          flex: 1,
          renderCell: (params: any) => (
            <div>
              {params.row.isAccessibleToAll ? (
                <FontAwesomeIcon icon={faCheck} color={"green"} />
              ) : (
                <FontAwesomeIcon icon={faX} color={"red"} />
              )}
            </div>
          ),
        },
        {
          field: "edit",
          headerName: "Edit",
          flex: 1,
          align: "center",
          headerAlign: "center",
          renderCell: (params: any) => (
            <Button
              variant="contained"
              style={{ boxShadow: "none", backgroundColor: "gray" }}
              onClick={() => handleEdit(params.row)}
            >
                Edit
            </Button>
          ),
        },
        {
          field: "delete",
          headerName: "Delete",
          headerAlign: "center",
          align: "center",
          flex: 1,
          renderCell: (params: any) => (
            <Button
              color="error"
              variant="contained"
              style={{boxShadow: "none", backgroundColor: '#db0000'}}
              onClick={() => {
                confirmDialog({
                  title: `Are you sure you want to delete ${params.row.formName}?`,
                  onOk: () => handleDelete(params.id),
                  okButtonProps: { danger: true },
                  okText: "YES",
                  cancelText: "NO",
                  centered: true,
                });
              }}
            >
                Delete
            </Button>
          ),
        },
    ];

    return (
        <Box sx={{ p: 4 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between"
                }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: "bold",
                        mb: 4
                    }}>
                    Web Forms
                </Typography>
                <Button
                    variant="contained"
                    style={{boxShadow: "none", height: 40}}
                    onClick={() => setIsOpen(true)}
                >
                    Add New Web Form
                </Button>
            </Box>

            <Box
                sx={{
                    width: "70%",
                    height: "840px",
                    margin: "0 auto",
                    "@media (max-width: 800px)": {
                        overflowX: "auto",
                        margin: 1,
                        minWidth: "95%",
                    },
                }}
            >
                <DataGrid
                    rows={webForms}
                    columns={columns}
                    disableRowSelectionOnClick
                    sx={{
                        "@media (max-width: 700px)": {
                            minWidth: "100%",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            color: "#333",
                            fontWeight: "bold",
                            fontSize: 15,
                            backgroundColor: "#f0f0f0",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: "bold",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#f1f1f1",
                        },
                    }}
                />
            </Box>

            <AppModal
                title={`Add new Web Form`}
                onCancel={() => {
                    setIsOpen(false);
                    reset({
                        formName: '',
                        formLink: '',
                        isAccessibleToAll: false,
                    })
                }}
                open={isOpen}
                footer={[
                    <div className="center-button-div">
                        <Button
                            variant="contained"
                            sx={{boxShadow: "none", mb: 2}}
                            onClick={handleSubmit(onSubmit)}
                        >
                            Add Web Form
                        </Button>
                    </div>,
                ]}
            >
                <Box
                    sx={{
                        mt: 1,
                        gap: 3,
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "1fr"}
                    }}>
                    <TextField
                        size="small"
                        placeholder="Form Name"
                        label="Form Name"
                        {...register("formName")}
                    />
                    <TextField
                        size="small"
                        placeholder="Form Link"
                        label="Form Link"
                        {...register("formLink")}
                    />
                    <Typography sx={{ fontSize: 17 }}>
                        Accessible To All:{" "}
                        <Controller
                            name="isAccessibleToAll"
                            control={control}
                            render={({ field }) => <Checkbox {...field} checked={field.value} />}
                        />
                    </Typography>
                </Box>
            </AppModal>

            <AppModal
                title={`Edit Web Form`}
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false);
                    setFormToEdit(null);
                }}
                footer={[
                    <div className="center-button-div">
                        <Button
                            variant="contained"
                            sx={{boxShadow: "none", mb: 2}}
                            onClick={handleEditSubmit(handleUpdate)}
                        >
                            Save Changes
                        </Button>
                    </div>
                ]}
            >
                <Box
                    sx={{
                        mt: 1,
                        gap: 3,
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "1fr"}
                    }}>
                    <TextField
                        size="small"
                        placeholder="Form Name"
                        label="Form Name"
                        {...editRegister("formName")}
                    />
                    <TextField
                        size="small"
                        placeholder="Form Link"
                        label="Form Link"
                        {...editRegister("formLink")}
                    />
                    <Typography sx={{ fontSize: 17 }}>
                        Accessible To All:{" "}
                        <Controller
                            name="isAccessibleToAll"
                            control={controlEdit}
                            render={({ field }) => <Checkbox {...field} checked={field.value} />}
                        />
                    </Typography>
                </Box>
            </AppModal>
        </Box>
    );
};

export default WebForms;
